import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import TaskService from '../../services/TaskService';
import InventoryService from '../../services/InventoryService';
import HouseService from '../../services/HouseService';
import TaskForm from '../../components/v2/CreateTask/TaskForm';
import {
    SEASON_ANCHORS,
    RECURRING_INTERVALS,
} from '../../components/v2/CreateTask/ScheduleSection';
import { localDateToISO, isoToLocalDate, todayLocalDate } from '../../utils/dateHelpers';
import { toast as Toast, ConfirmationModal } from '../../components/v2/V2Components';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

export const buildInitialState = (prefill = {}) => ({
    title: '',
    description: '',
    category: 'general',
    // Priority + status default to the canonical VALUE form returned by
    // GET /tasks/config (e.g. 'Medium' / 'To Do'). The dropdown options
    // are now config-driven, so the initial state must match exactly or
    // the <select> renders with no selection. Backend parse_enum accepts
    // either form, but keeping the wire format canonical removes the
    // need for that fallback in the common path.
    priority: 'Medium',
    status: 'To Do',
    mode: 'one-time',
    due_date: todayLocalDate(),
    recurrence_interval: 'weekly',
    recurrence_value: 1,
    season: 'spring',
    house_id: prefill.house_id ? String(prefill.house_id) : '',
    room_id: prefill.room_id ? String(prefill.room_id) : '',
    item_id: prefill.item_id ? String(prefill.item_id) : '',
    estimated_duration: '',
});

// Interval strings the form can edit natively (daily/weekly/monthly/yearly).
const NATIVE_RECURRING = new Set(RECURRING_INTERVALS.map(i => i.value));

/** Derive the form's mode + recurrence inputs from a Task loaded for edit. */
export const taskToFormState = (task) => {
    const due = isoToLocalDate(task.due_date) || todayLocalDate();

    let mode = 'one-time';
    let recurrence_interval = 'weekly';
    let recurrence_value = 1;
    let season = 'spring';

    // ADR-0009: the backend serializes frequency as one of three MODES —
    // "One Time" / "Recurring" / "Seasonal". The schedule detail (interval +
    // interval_value) lives solely in the recurrence JSONB.
    const freq = (task.frequency || '').toString().trim().toLowerCase().replace(/\s+/g, '-');

    if (task.season || freq === 'seasonal') {
        mode = 'seasonal';
        season = task.season || 'spring';
    } else if (task.is_recurring || freq === 'recurring') {
        mode = 'recurring';
        const interval = (task.recurrence?.interval || 'WEEKLY').toString().toLowerCase();
        recurrence_interval = NATIVE_RECURRING.has(interval) ? interval : 'weekly';
        recurrence_value = task.recurrence?.interval_value ?? 1;
    }

    // Title-case the priority so it matches the canonical VALUE form
    // ('Low' / 'Medium' / 'High') that GET /tasks/config returns. Tasks
    // saved before this change may have a lowercase value persisted in
    // the form's state cache; normalising at load is cheap and means the
    // dropdown always has a matching option.
    const rawPriority = (task.priority || 'Medium').toString();
    const priority = rawPriority.charAt(0).toUpperCase() + rawPriority.slice(1).toLowerCase();

    return {
        title: task.title || '',
        description: task.description || '',
        category: task.category || 'general',
        priority,
        status: task.status || 'To Do',
        mode,
        due_date: due,
        recurrence_interval,
        recurrence_value,
        season,
        house_id: task.house_id ? String(task.house_id) : '',
        room_id: task.room_id ? String(task.room_id) : '',
        item_id: task.item_id ? String(task.item_id) : '',
        estimated_duration: task.estimated_duration != null ? String(task.estimated_duration) : '',
    };
};

/** Build the JSON payload sent to POST /tasks or PUT /tasks/:id. */
export const buildPayload = (formData, isEditMode) => {
    const base = {
        title: formData.title.trim(),
        description: formData.description ? formData.description.trim() : null,
        category: formData.category,
        priority: formData.priority,
        house_id: formData.house_id ? parseInt(formData.house_id, 10) : null,
        room_id: formData.room_id ? parseInt(formData.room_id, 10) : null,
        item_id: formData.item_id ? parseInt(formData.item_id, 10) : null,
        estimated_duration: formData.estimated_duration !== ''
            ? parseInt(formData.estimated_duration, 10)
            : null,
    };
    if (isEditMode) base.status = formData.status;

    if (formData.mode === 'one-time') {
        return {
            ...base,
            frequency: 'one-time',
            is_recurring: false,
            due_date: localDateToISO(formData.due_date),
            season: null,
        };
    }
    if (formData.mode === 'recurring') {
        // ADR-0009: the mode is the only frequency concept; the schedule is
        // carried solely by interval + interval_value. The old quarterly /
        // yearly<->annual reverse-mapping is gone with the named cadences.
        return {
            ...base,
            frequency: 'recurring',
            is_recurring: true,
            due_date: localDateToISO(formData.due_date),
            recurrence_interval: formData.recurrence_interval.toUpperCase(),
            recurrence_value: parseInt(formData.recurrence_value, 10) || 1,
            season: null,
        };
    }
    // Seasonal — send the INTENT only (season). The backend's Season
    // resolver derives the due date (ADR-0009); the form never writes it.
    return {
        ...base,
        frequency: 'seasonal',
        is_recurring: true,
        season: formData.season,
    };
};

export const validate = (formData) => {
    if (!formData.title || !formData.title.trim()) {
        return 'Task title is required';
    }
    if (formData.mode !== 'seasonal' && !formData.due_date) {
        return 'Due date is required';
    }
    if (formData.mode === 'seasonal' && !formData.season) {
        // Symmetric with the backend guard: a SEASONAL task without a chosen
        // season is rejected at the API layer too.
        return 'Please pick a season';
    }
    if (formData.mode === 'recurring') {
        const v = parseInt(formData.recurrence_value, 10);
        if (!v || v < 1) return 'Interval must be at least 1';
    }
    return null;
};

const CreateTask = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const isEditMode = !!id;

    usePageView(ANALYTICS_EVENTS.MAINTENANCE_TASK_FORM_VIEWED, { is_edit_mode: isEditMode });

    const prefill = location.state || {};
    const returnTo = prefill.returnTo || '/maintenance';

    const initialState = useMemo(() => buildInitialState(prefill), []); // eslint-disable-line react-hooks/exhaustive-deps

    const [formData, setFormData] = useState(initialState);
    const [houses, setHouses] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(isEditMode);
    const [isDirty, setIsDirty] = useState(false);
    const [pendingDiscardDest, setPendingDiscardDest] = useState(null);

    const pristineRef = useRef(initialState);

    // Load rooms, items, and the task-enum config for the form. We pull the
    // aggregate `/tasks/config` so the same round-trip covers categories,
    // priorities, and statuses — every dropdown that maps to a backend
    // enum renders from API truth instead of a hardcoded list. If the
    // backend renames "To Do" → "Pending" or adds a new TaskPriority,
    // the form picks it up automatically (TaskForm has fallbacks for the
    // first paint / network failure case).
    useEffect(() => {
        let cancelled = false;
        const emptyConfig = { categories: [], frequencies: [], priorities: [], statuses: [], seasons: [] };
        Promise.all([
            InventoryService.getRooms().catch(() => []),
            InventoryService.getAllItems().catch(() => []),
            TaskService.getConfig().catch(() => emptyConfig),
            HouseService.getHouses().catch(() => []),
        ]).then(([roomsData, itemsData, configData, housesData]) => {
            if (cancelled) return;
            setRooms(Array.isArray(roomsData) ? roomsData : []);
            setItems(Array.isArray(itemsData) ? itemsData : []);
            setHouses(Array.isArray(housesData) ? housesData : []);
            const config = configData && typeof configData === 'object' ? configData : emptyConfig;
            setCategories(Array.isArray(config.categories) ? config.categories : []);
            setPriorities(Array.isArray(config.priorities) ? config.priorities : []);
            setStatuses(Array.isArray(config.statuses) ? config.statuses : []);
        });
        return () => { cancelled = true; };
    }, []);

    // Edit mode: load the task and reverse-map onto the form.
    useEffect(() => {
        if (!isEditMode) return;
        let cancelled = false;
        TaskService.getTask(id)
            .then(task => {
                if (cancelled) return;
                const loaded = taskToFormState(task);
                setFormData(loaded);
                pristineRef.current = loaded;
            })
            .catch(err => {
                console.error('Failed to load task for editing', err);
                Toast.error('Failed to load task for editing.');
            })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
    }, [id, isEditMode]);

    // Dirty tracking against pristine snapshot. Compare the *payload* the form
    // would send, not the raw formData, so cosmetic coercions like '1' vs 1
    // don't trigger a discard prompt.
    useEffect(() => {
        const a = JSON.stringify(buildPayload(formData, isEditMode));
        const b = JSON.stringify(buildPayload(pristineRef.current, isEditMode));
        setIsDirty(a !== b);
    }, [formData, isEditMode]);

    // Browser-level unsaved-changes guard.
    useEffect(() => {
        const beforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, [isDirty]);

    const handleFormChange = useCallback((patch) => {
        setFormData(prev => ({ ...prev, ...patch }));
    }, []);

    const handleNavigation = (dest = returnTo) => {
        if (isDirty) setPendingDiscardDest(dest);
        else navigate(dest);
    };

    const confirmDiscard = () => {
        const dest = pendingDiscardDest;
        setPendingDiscardDest(null);
        setIsDirty(false);
        navigate(dest ?? returnTo);
    };

    const handleSave = async () => {
        const error = validate(formData);
        if (error) {
            Toast.error(error);
            return;
        }
        setIsSaving(true);
        try {
            const payload = buildPayload(formData, isEditMode);
            if (isEditMode) {
                await TaskService.updateTask(id, payload);
                Toast.success('Task updated');
            } else {
                await TaskService.createTask(payload);
                Toast.success('Task created');
            }
            setIsDirty(false);
            navigate(returnTo);
        } catch (err) {
            console.error('Failed to save task', err);
            Toast.error('Failed to save task. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-[var(--bg-main)] text-[var(--text-main)] pb-24 md:pb-12 font-['Plus_Jakarta_Sans'] theme-transition">
            <main className="max-w-6xl mx-auto px-4 md:px-12 mt-8 md:mt-12">
                <div className="mb-8 p-1">
                    <span className="text-xs font-bold tracking-widest text-[var(--text-muted)] uppercase mb-2 block font-['Plus_Jakarta_Sans']">
                        {isEditMode ? 'Edit Task' : 'New Task'}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-extrabold text-[var(--primary)] font-['Manrope']">
                        {isEditMode ? 'Edit Maintenance Task' : 'Create Maintenance Task'}
                    </h2>
                </div>

                <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-start">
                    <div className="w-full lg:flex-[2] space-y-8 order-2 lg:order-1">
                        <TaskForm
                            formData={formData}
                            onChange={handleFormChange}
                            houses={houses}
                            rooms={rooms}
                            items={items}
                            categories={categories}
                            priorities={priorities}
                            statuses={statuses}
                            isEditMode={isEditMode}
                        />

                        <div className="pt-6 flex flex-col md:flex-row gap-4">
                            <button
                                onClick={handleSave}
                                disabled={isSaving || isLoading}
                                className="w-full md:w-auto px-10 py-4 bg-[var(--primary)] text-[var(--primary-text)] rounded-full font-bold text-lg hover:opacity-90 transition-all shadow-[var(--shadow)] flex items-center justify-center gap-2 disabled:opacity-70 theme-transition"
                            >
                                {isSaving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Save Task')}
                            </button>
                            <button
                                onClick={() => handleNavigation()}
                                disabled={isSaving}
                                className="w-full md:w-auto px-10 py-4 bg-transparent text-[var(--secondary)] border border-[var(--border-visible)] rounded-full font-bold text-lg hover:bg-[var(--bg-hover)] transition-all theme-transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>

                    {/* Tips card — equivalent of AddAsset's SmartAutofill slot. */}
                    <aside className="w-full lg:flex-[1] order-1 lg:order-2">
                        <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border)] shadow-[var(--shadow-sm)] p-6 md:p-8 theme-transition">
                            <div className="flex items-center gap-2 mb-4">
                                <Sparkles size={18} className="text-[var(--primary)]" />
                                <h4 className="text-sm font-bold tracking-widest text-[var(--text-muted)] uppercase">
                                    Tips
                                </h4>
                            </div>
                            <ul className="space-y-3 text-sm text-[var(--text-secondary)] leading-relaxed">
                                <li><span className="font-semibold text-[var(--text-main)]">One-time</span> — a single chore with a fixed date.</li>
                                <li><span className="font-semibold text-[var(--text-main)]">Recurring</span> — pick daily / weekly / monthly and an &quot;every N&quot; interval.</li>
                                <li><span className="font-semibold text-[var(--text-main)]">Seasonal</span> — anchored to {Object.values(SEASON_ANCHORS).map(s => s.label).join(', ')}; the due date is computed for you.</li>
                            </ul>
                        </div>
                    </aside>
                </div>
            </main>

            <ConfirmationModal
                isOpen={pendingDiscardDest !== null}
                onClose={() => setPendingDiscardDest(null)}
                onConfirm={confirmDiscard}
                title="Discard unsaved changes?"
                message="You have unsaved changes that will be lost if you leave this page."
                confirmText="Discard"
                cancelText="Keep editing"
                isDanger
            />
        </div>
    );
};

export default CreateTask;
